import { ReactNode } from "react";

import loGet from "lodash/get";
import { UseFormReturn } from "react-hook-form";

import AiGenerator from "@/components/forms/AiGenerator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface EditorInputFieldProps {
  form: UseFormReturn<any>;
  fieldName: string;
  label?: string;
  type?: "text" | "number" | "color" | "textarea";
  aiPrompt?: Record<string, unknown>;
  isFreeform?: boolean;
  condense?: boolean;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  textareaClassName?: string;
  inputClassName?: string;
  icon?: ReactNode;
  hideError?: boolean;
  hideLabel?: boolean;
  hideErrorMessage?: boolean;
}

// Allows the addition of extra props to the EditorInputField component,
// which are captured in the ...props parameter.
type EditorInputFieldPropsWithExtras = EditorInputFieldProps & Record<string, any>;

const EditorInputField = ({
  form,
  fieldName,
  label = "",
  type = "text",
  aiPrompt = undefined,
  isFreeform = false,
  condense = false,
  placeholder = "",
  className = "",
  labelClassName = "",
  textareaClassName = "",
  inputClassName = "",
  icon = null,
  hideError = false,
  hideLabel = false,
  hideErrorMessage = false,
  ...props
}: EditorInputFieldPropsWithExtras) => {
  const placeholderText = placeholder || label;

  let error: string | null = null;
  if (!hideError) {
    const fieldError = loGet(form.formState.errors, fieldName);
    error =
      fieldError && typeof fieldError === "object" && "message" in fieldError ? (fieldError.message as string) : null;
  }

  return (
    <div className={cn("relative focus-within:text-ash-900 text-ash-500", condense ? "mb-2" : "mb-6 -mt-2", className)}>
      {!hideLabel && (
        <Label htmlFor={fieldName} className={cn("text-ash-800 font-medium text-sm", labelClassName)}>
          {label}
        </Label>
      )}

      <div className="relative">
        {icon && <div className="absolute left-4 top-1/2 -translate-y-1/2">{icon}</div>}

        {type === "text" || type === "number" ? (
          <Input
            {...form.register(fieldName)}
            id={fieldName}
            placeholder={placeholderText}
            type={type}
            className={cn(
              "placeholder:text-ash-500",
              error && "border-lava-500 focus-visible:ring-lava-500",
              aiPrompt && "pr-9",
              icon && "pl-10",
              inputClassName
            )}
            {...props}
          />
        ) : type === "color" ? (
          <div className="flex flex-col gap-2 rounded-xl">
            {/* Color picker updates the form value */}
            <input
              {...form.register(fieldName)}
              id={fieldName}
              type="color"
              className={cn(
                "cursor-pointer w-full h-14 rounded-xl p-1",
                error && "border-lava-500 focus-visible:ring-lava-500",
                inputClassName
              )}
            />
            {/* Hex input for manual color input / copy pasting */}
            <input
              id={`${fieldName}-hex-input`}
              type="text"
              onChange={(e) => {
                // Make sure it always starts with a #
                const color = e.target.value;
                if (color.startsWith("#")) {
                  form.setValue(fieldName, color, { shouldDirty: true });
                } else {
                  form.setValue(fieldName, `#${color}`, { shouldDirty: true });
                }
                // check if valid hex, if not show error
                if (!/^(#|)[0-9A-F]{6}$/i.test(color)) {
                  form.setError(fieldName, { message: "Invalid hex color" });
                } else {
                  form.clearErrors(fieldName);
                }
              }}
              value={form.watch(fieldName)}
              onClick={(e) => {
                (e.target as HTMLInputElement).select();
              }}
            />
          </div>
        ) : (
          <Textarea
            {...form.register(fieldName)}
            id={fieldName}
            placeholder={placeholderText}
            className={cn(
              "h-32 py-3 px-4 rounded-lg bg-white border border-ash-300 placeholder:text-ash-500 text-sm font-normal",
              aiPrompt && "pr-9",
              textareaClassName
            )}
            {...props}
          />
        )}
      </div>
      {error && !hideErrorMessage && <div className="mt-0.5 text-xs text-lava-500">{error}</div>}

      {aiPrompt && <AiGenerator form={form} fieldName={fieldName} aiPrompt={aiPrompt} isFreeform={isFreeform} />}
    </div>
  );
};

export default EditorInputField;
